import { useMemo, useState } from "react";
import { calculateSipJourney, formatAsCrore } from "creso-core/utils";
import { AMOUNT_INPUT_MAX } from "./config";
import { CalculatorInput } from "./calculator-input";
import { CalculatorSnapshotContainer } from "./snapshot-container";
import { CalculatorInputSummary } from "./input-summary";
import { CalculatorResultContainer } from "./result-container";
import { CalculatorResultSummary } from "./result-summary";
import { CalculatorChart } from "./calculator-chart";
import { Disclaimer } from "./disclaimer";
import { SnapshotButton } from "./snapshot-btn";

const DEFAULTS = {
  monthlyAmount: 10_000,
  expectedReturn: 12,
  duration: 10,
  stepUp: 0,
};

export function SIPReturnsCalculator({
  brandRender,
}: {
  brandRender: () => React.ReactNode;
}) {
  const [monthlyAmount, setMonthlyAmount] = useState(DEFAULTS.monthlyAmount);
  const [expectedReturn, setExpectedReturn] = useState(DEFAULTS.expectedReturn);
  const [duration, setDuration] = useState(DEFAULTS.duration);
  const [stepUp, setStepUp] = useState(DEFAULTS.stepUp);

  const { maturityValue, totalInvested, gains, chartData } = useMemo(() => {
    const journey = calculateSipJourney(
      monthlyAmount,
      expectedReturn,
      duration * 12,
      { percentage: stepUp, frequencyMonths: 12 }
    );
    const last = journey.at(-1);
    const corpus = last?.expectedValue ?? 0;
    const invested = last?.cumulativeInvested ?? 0;

    return {
      maturityValue: Math.round(corpus),
      totalInvested: Math.round(invested),
      gains: Math.round(corpus - invested),
      chartData: journey.map((period, index) => ({
        label: `Y${index + 1}`,
        invested: period.cumulativeInvested,
        gains: Math.max(0, period.expectedValue - period.cumulativeInvested),
      })),
    };
  }, [monthlyAmount, expectedReturn, duration, stepUp]);

  const summaryItems = [
    { label: "Total Invested", value: formatAsCrore(totalInvested) },
    { label: "Total Returns", value: formatAsCrore(gains) },
    {
      label: "Maturity Value",
      value: formatAsCrore(maturityValue),
      highlight: true,
    },
  ];

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-6">
        <CalculatorInput
          label="Monthly Amount"
          value={monthlyAmount}
          onChange={setMonthlyAmount}
          min={500}
          max={10_00_000}
          inputMax={AMOUNT_INPUT_MAX}
          step={500}
          unit="₹"
          formatDisplay={(v) => formatAsCrore(v, 1, true)}
        />
        <CalculatorInput
          label="Expected Returns"
          value={expectedReturn}
          onChange={setExpectedReturn}
          min={1}
          max={50}
          step={0.5}
          unit="%"
        />
        <CalculatorInput
          label="Duration"
          value={duration}
          onChange={setDuration}
          min={1}
          max={40}
          step={1}
          unit="yr"
        />
        <CalculatorInput
          label="Annual Step Up"
          value={stepUp}
          onChange={setStepUp}
          min={0}
          max={50}
          step={1}
          unit="%"
        />
      </div>

      <CalculatorSnapshotContainer
        brandRender={brandRender}
        inputRender={() => (
          <CalculatorInputSummary
            header="SIP returns calculation"
            fields={[
              { label: "Monthly Amount", value: formatAsCrore(monthlyAmount) },
              { label: "Expected Returns", value: `${expectedReturn}%` },
              { label: "Annual Step Up", value: `${stepUp}%` },
              { label: "Duration", value: `${duration} year(s)` },
            ]}
          />
        )}
        resultRender={({ onSnapshot }) => (
          <CalculatorResultContainer>
            <CalculatorResultSummary items={summaryItems} />
            <div className="min-h-85 w-full pt-12 relative mb-2">
              <SnapshotButton onSnapshot={onSnapshot} />
              <CalculatorChart data={chartData} />
            </div>
            <Disclaimer />
          </CalculatorResultContainer>
        )}
      />
    </div>
  );
}
